/**
 * THE BROWSER SIDE OF PAYMENTS, and the only place rupees exist. Every rupee
 * here comes out of integer arithmetic on paise: the whole part and the
 * hundredths are formatted separately and joined, never divided into a float.
 *
 * THE TYPES BELOW ARE DECLARED TWICE, and the reason is the build. `admin/src`
 * compiles in the `web` project and `admin/functions` in the `worker` project,
 * so neither may import the other, not even for a type (TS6307). This is the
 * browser's half of one wire; `functions/api/payments.ts` is the server's, and
 * `app/tests/admin-payments.test.ts` asserts the payload field by field
 * against a real database. `PAYMENTS_HANDOFF.md` has the config change that
 * would collapse them.
 */

export type PlanView = {
  readonly planId: string;
  readonly name: string;
  readonly blurb: string;
  readonly amountPaise: number;
  readonly periodDays: number;
  readonly active: boolean;
  readonly sortOrder: number;
  readonly createdAt: number;
  readonly subscriptions: number;
};

export type PaymentView = {
  readonly paymentId: string;
  readonly userId: string;
  readonly userEmail: string | null;
  readonly planName: string | null;
  readonly amountPaise: number;
  readonly refundedPaise: number;
  readonly status: "created" | "captured" | "failed" | "refunded";
  readonly gateway: string;
  readonly gatewayRef: string | null;
  readonly createdAt: number;
};

export type SubscriptionView = {
  readonly subscriptionId: string;
  readonly userId: string;
  readonly userEmail: string | null;
  readonly planId: string;
  readonly planName: string | null;
  readonly status: "active" | "cancelled" | "expired" | "granted";
  readonly startedAt: number;
  readonly expiresAt: number;
  readonly grantedBy: string | null;
};

export type VoucherView = {
  readonly voucherId: string;
  readonly code: string;
  readonly percentOff: number | null;
  readonly amountOffPaise: number | null;
  readonly maxUses: number;
  readonly usedCount: number;
  readonly expiresAt: number | null;
  readonly createdAt: number;
  readonly createdByEmail: string | null;
};

export type WeekBucket = {
  readonly week: string;
  readonly capturedPaise: number;
  readonly refundedPaise: number;
  readonly payments: number;
};

export type PlanBreakdown = {
  readonly planId: string;
  readonly name: string;
  readonly capturedPaise: number;
  readonly refundedPaise: number;
  readonly netPaise: number;
  readonly payments: number;
  readonly liveSubscriptions: number;
};

export type LedgerPage = {
  readonly page: number;
  readonly pages: number;
  readonly pageSize: number;
  readonly total: number;
  readonly status: string;
  readonly gateway: string;
};

export type PaymentsPayload = {
  readonly ok: true;
  readonly gateway: {
    readonly name: string;
    readonly movesRealMoney: boolean;
    readonly liveCredentials: boolean;
  };
  readonly currency: "INR";
  readonly now: number;
  readonly totals: {
    readonly payments: number;
    readonly grossPaise: number;
    readonly refundedPaise: number;
    readonly netPaise: number;
    readonly failed: number;
  };
  readonly passes: {
    readonly liveNow: number;
    readonly byStatus: readonly { readonly status: string; readonly count: number }[];
  };
  readonly gatewayMix: readonly { readonly gateway: string; readonly count: number }[];
  readonly statusMix: readonly { readonly status: string; readonly count: number }[];
  readonly ledger: LedgerPage;
  readonly breakdown: readonly PlanBreakdown[];
  readonly weeks: readonly WeekBucket[];
  readonly plans: readonly PlanView[];
  readonly payments: readonly PaymentView[];
  readonly subscriptions: readonly SubscriptionView[];
  readonly vouchers: readonly VoucherView[];
};

export const RUPEE = "₹";

const GROUPING = new Intl.NumberFormat("en-IN");
const WHEN = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" });

export function formatPaise(paise: number): string {
  if (!Number.isSafeInteger(paise)) return `${RUPEE}0.00`;
  const sign = paise < 0 ? "-" : "";
  const absolute = Math.abs(paise);
  const whole = GROUPING.format(Math.trunc(absolute / 100));
  const hundredths = String(absolute % 100).padStart(2, "0");
  return `${sign}${RUPEE}${whole}.${hundredths}`;
}

/** What a person typed, as paise, or null. A third decimal is a fraction of a paisa. */
export function parseRupeesToPaise(value: string): number | null {
  const cleaned = value.trim().replace(/^₹/, "").replace(/,/g, "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;

  const dot = cleaned.indexOf(".");
  const whole = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const fraction = dot === -1 ? "" : cleaned.slice(dot + 1);
  const paise = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(paise) ? paise : null;
}

export const VOUCHER_CODE = /^[A-Z0-9_-]{4,40}$/;

export function normaliseCode(value: string): string {
  return value.trim().toUpperCase();
}

export function isVoucherCode(value: string): boolean {
  return VOUCHER_CODE.test(normaliseCode(value));
}

export function formatWhen(ms: number | null): string {
  return ms === null || ms <= 0 ? "never" : WHEN.format(new Date(ms));
}

/** Rupees as a bare decimal, for a cell a spreadsheet has to add up. */
export function rupeesPlain(paise: number): string {
  if (!Number.isSafeInteger(paise)) return "0.00";
  const sign = paise < 0 ? "-" : "";
  const absolute = Math.abs(paise);
  return `${sign}${Math.trunc(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
}

/** Where a voucher stands, from its own two counters and its own expiry. */
export function voucherStanding(voucher: VoucherView, now: number): string {
  if (voucher.usedCount >= voucher.maxUses) return "Used up";
  if (voucher.expiresAt !== null && voucher.expiresAt <= now) return "Expired";
  return "Open";
}

export function passStanding(pass: SubscriptionView, now: number): string {
  if (pass.status === "cancelled") return "Cancelled";
  if (pass.expiresAt <= now) return "Ended";
  if (pass.status === "granted") return "Granted by an admin";
  return "In force";
}

export function daysLeft(expiresAt: number, now: number): number {
  return Math.max(0, Math.ceil((expiresAt - now) / 86_400_000));
}

/** A label for a status the ledger actually holds, never a list of our own. */
export function statusLabel(status: string): string {
  const words: Record<string, string> = {
    created: "Started",
    captured: "Captured",
    failed: "Failed",
    refunded: "Refunded",
  };
  return words[status] ?? status;
}

/** What it takes off, without inventing a basket to take it off. */
export function voucherBenefit(voucher: VoucherView): string {
  if (voucher.percentOff !== null) return `${voucher.percentOff}% off`;
  if (voucher.amountOffPaise !== null) return `${formatPaise(voucher.amountOffPaise)} off`;
  return "no benefit set";
}

export type PaymentAction =
  | {
      readonly action: "create_plan";
      readonly name: string;
      readonly blurb: string;
      readonly amountPaise: number;
      readonly periodDays: number;
      readonly sortOrder: number;
    }
  | { readonly action: "set_plan_active"; readonly planId: string; readonly active: boolean }
  | { readonly action: "mock_capture"; readonly email: string; readonly planId: string }
  | {
      readonly action: "refund";
      readonly paymentId: string;
      readonly amountPaise: number;
      readonly reason: string;
    }
  | {
      readonly action: "create_voucher";
      readonly code: string;
      readonly percentOff?: number;
      readonly amountOffPaise?: number;
      readonly maxUses: number;
      readonly expiresAt: number | null;
    }
  | {
      readonly action: "grant_pass";
      readonly email: string;
      readonly planId: string;
      readonly days: number;
    }
  | { readonly action: "extend_pass"; readonly subscriptionId: string; readonly days: number }
  | { readonly action: "cancel_pass"; readonly subscriptionId: string; readonly reason: string };

export class ApiError extends Error {}

async function readBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    throw new ApiError(`the server answered ${response.status} with nothing readable`);
  }
}

export type LedgerFilter = {
  readonly status: string;
  readonly gateway: string;
  readonly page: number;
};

export const EMPTY_LEDGER_FILTER: LedgerFilter = { status: "", gateway: "", page: 1 };

export function paymentsPath(filter: LedgerFilter): string {
  const params = new URLSearchParams();
  if (filter.status.length > 0) params.set("status", filter.status);
  if (filter.gateway.length > 0) params.set("gateway", filter.gateway);
  if (filter.page > 1) params.set("page", String(filter.page));
  const tail = params.toString();
  return tail.length === 0 ? "/api/payments" : `/api/payments?${tail}`;
}

export async function loadPayments(
  filter: LedgerFilter = EMPTY_LEDGER_FILTER,
): Promise<PaymentsPayload> {
  const response = await fetch(paymentsPath(filter), { credentials: "same-origin" });
  const body = await readBody(response);
  if (!response.ok || body["ok"] !== true) {
    throw new ApiError(String(body["error"] ?? response.status));
  }
  return body as unknown as PaymentsPayload;
}

export async function actOnPayments(action: PaymentAction): Promise<Record<string, unknown>> {
  const response = await fetch("/api/payments", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(action),
  });
  const body = await readBody(response);
  if (!response.ok || body["ok"] !== true) {
    throw new ApiError(String(body["error"] ?? response.status));
  }
  return body;
}
